import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageHero } from '@/components/PageHero';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { ScrollProgressBar } from '@/components/ScrollProgressBar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Calendar, Loader2 } from 'lucide-react';
import { BOX_IMAGES } from '@/data/images';
import { API } from '@/lib/api';

const EASE = [0.22, 1, 0.36, 1];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

export default function BlogListPage() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog/posts`);
        setPosts(data);
      } catch {
        setPosts([]);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen">
      <PageMeta
        title="Blog"
        description="Blog CrossFit Saint-Chamond : guides, conseils techniques, programmes d'entraînement HYROX et CrossFit. Rédigé par Nicolas Perroy, coach certifié."
        path="/blog"
      />
      <BreadcrumbJsonLd items={[{ name: 'Accueil', url: '/' }, { name: 'Blog' }]} />
      <ScrollProgressBar />
      <TopBar />
      <Navigation />

      <PageHero
        pageKey="/blog"
        eyebrow="Blog"
        title="CONSEILS & GUIDES"
        highlight="GUIDES"
        subtitle="Articles rédigés par Nicolas Perroy : technique, nutrition, préparation HYROX, progression CrossFit. Du contenu utile pour progresser."
        image={BOX_IMAGES.kettlebellWoman}
        alt="Blog CrossFit Saint-Chamond"
        breadcrumbs={[{ name: 'Accueil', href: '/' }, { name: 'Blog' }]}
        minHeight="min-h-[55vh]"
      />

      <section className="section-padding bg-background" data-testid="blog-list">
        <div className="container-custom">
          {posts === null ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <Card className="p-12 text-center rounded-2xl">
              <p className="text-muted-foreground">Aucun article publié pour le moment. Reviens bientôt !</p>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, idx) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-5%' }}
                  transition={{ duration: 0.7, delay: idx * 0.08, ease: EASE }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="group block h-full"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <Card className="h-full overflow-hidden rounded-2xl border-border hover:border-primary/40 hover:shadow-[0_20px_60px_-15px_hsla(155,75%,18%,0.25)] transition-all duration-500 hover:-translate-y-1">
                      {post.cover_image && (
                        <div className="relative h-52 overflow-hidden bg-muted">
                          <img decoding="async"
                            src={post.cover_image}
                            alt={post.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-dark/60 to-transparent" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          {(post.tags || []).slice(0, 2).map((t) => (
                            <Badge key={t} className="bg-primary/10 hover:bg-primary/15 text-primary border-primary/20 text-[10px] tracking-wider">
                              {t}
                            </Badge>
                          ))}
                          <span className="text-[10px] text-muted-foreground tracking-widest uppercase flex items-center gap-1 ml-auto">
                            <Calendar className="h-3 w-3" />
                            {formatDate(post.created_at)}
                          </span>
                        </div>
                        <h2 className="font-heading text-xl text-dark mb-3 tracking-wide leading-tight group-hover:text-primary transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground text-sm leading-relaxed mb-5 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center gap-1.5 text-primary font-semibold text-xs tracking-[0.18em] uppercase">
                          <span className="link-underline">Lire l'article</span>
                          <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
